// src/components/OutOfCreditsModal.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const modalCss = `
.credits-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.credits-modal {
  width: 100%;
  max-width: 400px;
  background: #ffffff;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-family: system-ui, -apple-system, sans-serif;
  text-align: center;
  position: relative;
  animation: cm-pop 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes cm-pop {
  from { opacity: 0; transform: scale(0.96) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

.credits-modal-icon {
  font-size: 32px;
  margin-bottom: 12px;
  display: inline-block;
}

.credits-modal-title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 800;
  color: #111827;
}

.credits-modal-text {
  margin: 0 0 24px;
  font-size: 15px;
  line-height: 1.5;
  color: #4b5563;
}

.credits-modal-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.credits-modal-btn {
  width: 100%;
  border-radius: 12px;
  padding: 12px;
  font-size: 15px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.credits-modal-btn.primary {
  background: linear-gradient(135deg, #6E5BFF, #8B5CF6);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(110, 91, 255, 0.25);
}
.credits-modal-btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(110, 91, 255, 0.35);
}

.credits-modal-btn.secondary {
  background: #fff;
  border: 1px solid #e5e7eb;
  color: #111827;
}
.credits-modal-btn.secondary:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.credits-modal-btn.ghost {
  background: transparent;
  color: #6b7280;
  font-weight: 500;
  padding: 8px;
}
.credits-modal-btn.ghost:hover {
  color: #374151;
}

.credits-modal-status {
  margin-top: 12px;
  padding: 8px 12px;
  background: #ecfdf5;
  color: #047857;
  font-size: 13px;
  border-radius: 8px;
  font-weight: 500;
  animation: cm-fade 0.2s ease;
}
@keyframes cm-fade { from { opacity: 0; } to { opacity: 1; } }
`;

export default function OutOfCreditsModal({ open, onClose }) {
  const nav = useNavigate();
  const [inviteLoading, setInviteLoading] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (open) setStatus("");
  }, [open]);

  if (!open) return null;

  const handleInvite = async () => {
    try {
      setInviteLoading(true);
      setStatus("");

      const res = await fetch("/api/referrals/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        console.error("Invite Error Details:", res.status, errData);
        throw new Error(errData.error || `Server Error: ${res.status}`);
      }

      const data = await res.json();
      const inviteUrl = data.url;

      try {
        await navigator.clipboard.writeText(inviteUrl);
        setStatus("Link copied! You both get +10 credits when they sign up.");
      } catch (copyErr) {
        window.prompt("Copy this invite link:", inviteUrl);
        setStatus("Share this link. You both get +10 credits!");
      }
    } catch (e) {
      console.error(e);
      setStatus(`Error: ${e.message}`);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleUpgrade = () => {
    nav("/pricing?plan=outreach");
    onClose?.();
  };

  return (
    <>
      <style>{modalCss}</style>
      <div className="credits-modal-backdrop" role="dialog" aria-modal="true">
        <div className="credits-modal">
          <div className="credits-modal-icon"><img
            className="brand-icon"
            src="https://www.musicbase.io/logo.png"
            alt=""
            aria-hidden
            style={{ height: "25px", width: "25px" }}
          /></div>
          <h2 className="credits-modal-title">Need more credits?</h2>
          <p className="credits-modal-text">
            Invite a friend and you'll both get <strong>+10 Reveal credits</strong> and <strong>+3 Search limits</strong>.
          </p>

          <div className="credits-modal-actions">
            <button
              type="button"
              className="credits-modal-btn primary"
              onClick={handleInvite}
              disabled={inviteLoading}
            >
              {inviteLoading ? "Generating..." : "Invite a Friend"}
            </button>

            <button
              type="button"
              className="credits-modal-btn secondary"
              onClick={handleUpgrade}
            >
              Upgrade Plan
            </button>

            <button
              type="button"
              className="credits-modal-btn ghost"
              onClick={() => onClose?.()}
            >
              Not Now
            </button>
          </div>

          {status && <div className="credits-modal-status">{status}</div>}
        </div>
      </div>
    </>
  );
}